import React, { useState, useEffect } from "react";
import { database } from "../firebaseConfig";
import { ref, onValue, set, remove, update } from "firebase/database";
import ConfirmModal from "./ConfirmModal";
import downArrow from "../img/down.svg";
import "./WorldSettings.css";
import "./ListCleaningRulesSettings.css";

const CLEANING_MODES = [
  {
    id: "standard",
    icon: "fa-wand-magic-sparkles",
    title: "Standard AI",
    text: "The AI keeps or removes contacts. 1 credit each.",
  },
  {
    id: "hyper",
    icon: "fa-bolt",
    title: "Hyper Intelligence",
    text: "Smarter KEEP/REMOVE for niche cases. 2 credits each.",
  },
  {
    id: "email",
    icon: "fa-at",
    title: "Email Check only",
    text: "Skip AI cleaning. Only check if emails are valid.",
  },
];

const getCleaningMode = (guideline) => {
  if (guideline.emailValidationOnly) return "email";
  if (guideline.hyperIntelligence) return "hyper";
  return "standard";
};

const formatCreatedAt = (createdAt) => {
  if (createdAt == null || createdAt === "") return null;
  const date =
    typeof createdAt === "object" && createdAt.seconds
      ? new Date(createdAt.seconds * 1000)
      : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};

const countGuidelineLines = (section) => {
  if (typeof section !== "string" || !section.trim()) return 0;
  return section
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("-") && line.replace(/^-+/, "").trim().length > 0).length;
};

// How well is this set filled in? Ranges 0–10. Deliberately lenient: the
// user just needs a business context and a handful of rules to hit a full
// score. It scores completeness, not quality.
const getGuidelineSetupScore = (guideline) => {
  let score = 0;

  if ((guideline?.name || "").trim().length > 0) score += 1;

  const context = (guideline?.businessContext || "").trim();
  if (context.length >= 40) score += 3;
  else if (context.length >= 1) score += 2;

  const keepCount = countGuidelineLines(guideline?.keepSection);
  if (keepCount >= 4) score += 3;
  else if (keepCount >= 2) score += 2;
  else if (keepCount >= 1) score += 1;

  const removeCount = countGuidelineLines(guideline?.removeSection);
  if (removeCount >= 4) score += 3;
  else if (removeCount >= 2) score += 2;
  else if (removeCount >= 1) score += 1;

  return Math.max(0, Math.min(10, score));
};

const getSetupScoreTone = (score) => {
  if (score >= 8) return "strong";
  if (score >= 5) return "medium";
  if (score >= 1) return "starter";
  return "empty";
};

const getSetupScoreLabel = (score) => {
  if (score >= 10) return "Fully set up";
  if (score >= 8) return "Well set up";
  if (score >= 5) return "Getting there";
  if (score >= 1) return "Just starting";
  return "Not set up";
};

const OptionToggle = ({ on, onToggle, icon, title, text }) => (
  <button type="button" className={`lcr-toggle ${on ? "is-on" : ""}`} onClick={onToggle}>
    <span className="lcr-toggleIcon">
      <i className={`fa ${icon}`} />
    </span>
    <span className="lcr-toggleText">
      <span className="lcr-toggleTitle">{title}</span>
      <span className="lcr-toggleSub">{text}</span>
    </span>
    <span className={`lcr-switch ${on ? "is-on" : ""}`} aria-hidden="true">
      <span className="lcr-switchKnob" />
    </span>
  </button>
);

// Standalone "List Cleaning Rules" configuration.
// Reads/writes the SAME Firebase data as the legacy AI-Settings block
// (`/World/${userId}/settings/cleanupGuidelinesList`) so the Cleaner can be
// configured here inside the Research Configuration instead of in AI Settings.
const ListCleaningRulesSettings = ({ userId }) => {
  const [cleanupGuidelinesList, setCleanupGuidelinesList] = useState([]);
  const [legacyGuidelines, setLegacyGuidelines] = useState([]);
  const [closeIntegration, setCloseIntegration] = useState(null);

  const [localGuidelineKeepSection, setLocalGuidelineKeepSection] = useState({});
  const [localGuidelineRemoveSection, setLocalGuidelineRemoveSection] = useState({});
  const [localGuidelineBusinessContext, setLocalGuidelineBusinessContext] = useState({});

  const [showAddCleanupGuideline, setShowAddCleanupGuideline] = useState(false);
  const [newGuidelineName, setNewGuidelineName] = useState("");
  const [expandedGuidelineId, setExpandedGuidelineId] = useState(null);

  const [message, setMessage] = useState("");
  const [confirmModal, setConfirmModal] = useState({ open: false, title: "", message: "", onConfirm: null });

  const flashMessage = (text, duration = 3000) => {
    setMessage(text);
    if (duration) setTimeout(() => setMessage(""), duration);
  };

  // ── Firebase Listener ──
  useEffect(() => {
    if (!userId) return;

    const closeIntegrationRef = ref(database, `/World/${userId}/integrations/crm/close`);
    const unsubscribeCloseIntegration = onValue(closeIntegrationRef, (snapshot) => {
      setCloseIntegration(snapshot.exists() ? snapshot.val() : null);
    });

    const cleanupGuidelinesRef = ref(database, `/World/${userId}/settings/cleanupGuidelinesList`);
    const unsubscribeGuidelines = onValue(
      cleanupGuidelinesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const guidelinesList = snapshot.val();
          const guidelinesArray = Object.keys(guidelinesList)
            .map((key) => ({ id: key, ...guidelinesList[key] }))
            .sort((a, b) => b.createdAt - a.createdAt);

          const modernGuidelines = guidelinesArray.filter(
            (g) => g.keepSection || g.removeSection || !g.content || g.content.trim() === ""
          );
          const legacy = guidelinesArray.filter(
            (g) => g.content && !g.keepSection && !g.removeSection && g.content.trim() !== ""
          );

          setCleanupGuidelinesList(modernGuidelines);
          setLegacyGuidelines(legacy);
        } else {
          const defaultGuideline = {
            id: "default",
            name: "Default Guidelines",
            content: `- Remove any redundant or repetitive phrases
- Ensure the text flows naturally and conversationally
- Fix any grammatical errors while maintaining a casual tone
- Remove any AI-like phrasing or overly formal language
- Make sure the email sounds like it was written by a real person
- Eliminate unnecessary pleasantries and corporate jargon
- Keep sentences short and direct
- Remove any closing phrases or signatures that might have been added`,
            isDefault: true,
            verifyDecisionMakerEmails: false,
            createdAt: Date.now(),
          };
          setCleanupGuidelinesList([defaultGuideline]);
        }
      },
      (error) => {
        console.error("Fehler beim Laden der Cleanup Guidelines Liste", error);
      }
    );

    return () => {
      unsubscribeCloseIntegration();
      unsubscribeGuidelines();
    };
  }, [userId]);

  // ── Handlers ──
  const handleAddCleanupGuideline = async () => {
    if (!newGuidelineName.trim()) {
      flashMessage("Bitte gib einen Namen für die Guideline ein.");
      return;
    }

    const defaultRemoveList = `- Doctors
- Lawyers
- Political parties
- Hospitals
- Police
- Legal businesses
- Tax-related businesses
- Military
- Charities
- Non-profit organizations
- Software developers
- Software companies
- City planning
- Government projects
- Insurance companies
- Law firms
- Advertising agencies
- Startup incubators
- Government-funded founder centers`;

    const newGuideline = {
      id: Date.now().toString(),
      name: newGuidelineName.trim(),
      keepSection: `- Add your specific target audience characteristics here
- Include company sizes, industries, and demographics
- When uncertain about a business type, default to keeping the contact`,
      removeSection: defaultRemoveList,
      businessContext: "",
      cleanNames: true,
      keepEmptyEmails: false,
      hyperIntelligence: false,
      verifyDecisionMakerEmails: false,
      isDefault: cleanupGuidelinesList.length === 0,
      createdAt: Date.now(),
    };

    try {
      const guidelineRef = ref(database, `/World/${userId}/settings/cleanupGuidelinesList/${newGuideline.id}`);
      await set(guidelineRef, newGuideline);

      setNewGuidelineName("");
      setShowAddCleanupGuideline(false);
      setExpandedGuidelineId(newGuideline.id);
      flashMessage("Neue Cleanup Guideline erfolgreich erstellt.");
    } catch (error) {
      console.error("Fehler beim Erstellen der Cleanup Guideline:", error);
      flashMessage("Fehler beim Erstellen der Cleanup Guideline.");
    }
  };

  const handleUpdateCleanupGuideline = async (guidelineId, field, value) => {
    await handleUpdateCleanupGuidelineFields(guidelineId, { [field]: value });
  };

  const handleUpdateCleanupGuidelineFields = async (guidelineId, fields) => {
    try {
      const updates = {};
      Object.entries(fields).forEach(([field, value]) => {
        updates[`/World/${userId}/settings/cleanupGuidelinesList/${guidelineId}/${field}`] = value;
      });
      await update(ref(database), updates);
      setCleanupGuidelinesList((prev) => prev.map((g) => (g.id === guidelineId ? { ...g, ...fields } : g)));
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Cleanup Guideline:", error);
      flashMessage("Fehler beim Aktualisieren der Cleanup Guideline.");
    }
  };

  const handleSetCleaningMode = async (guidelineId, mode) => {
    const fields =
      mode === "email"
        ? { emailValidationOnly: true, hyperIntelligence: false }
        : mode === "hyper"
          ? { emailValidationOnly: false, hyperIntelligence: true }
          : { emailValidationOnly: false, hyperIntelligence: false };
    await handleUpdateCleanupGuidelineFields(guidelineId, fields);
  };

  const handleLocalGuidelineKeepSectionChange = (guidelineId, value) => {
    setLocalGuidelineKeepSection((prev) => ({ ...prev, [guidelineId]: value }));
  };

  const handleGuidelineKeepSectionBlur = async (guidelineId, value) => {
    await handleUpdateCleanupGuideline(guidelineId, "keepSection", value);
    setLocalGuidelineKeepSection((prev) => {
      const newState = { ...prev };
      delete newState[guidelineId];
      return newState;
    });
  };

  const handleLocalGuidelineRemoveSectionChange = (guidelineId, value) => {
    setLocalGuidelineRemoveSection((prev) => ({ ...prev, [guidelineId]: value }));
  };

  const handleGuidelineRemoveSectionBlur = async (guidelineId, value) => {
    await handleUpdateCleanupGuideline(guidelineId, "removeSection", value);
    setLocalGuidelineRemoveSection((prev) => {
      const newState = { ...prev };
      delete newState[guidelineId];
      return newState;
    });
  };

  const handleLocalBusinessContextChange = (guidelineId, value) => {
    setLocalGuidelineBusinessContext((prev) => ({ ...prev, [guidelineId]: value }));
  };

  const handleBusinessContextBlur = async (guidelineId, value) => {
    await handleUpdateCleanupGuideline(guidelineId, "businessContext", value);
    setLocalGuidelineBusinessContext((prev) => {
      const newState = { ...prev };
      delete newState[guidelineId];
      return newState;
    });
  };

  const getGuidelineKeepSectionValue = (guideline) => {
    if (localGuidelineKeepSection[guideline.id] !== undefined) {
      return localGuidelineKeepSection[guideline.id];
    }
    if (guideline.keepSection) return guideline.keepSection;
    if (guideline.content) {
      const keepMatch = guideline.content.match(/KEEP[^]*?(?=REMOVE|$)/i);
      return keepMatch ? keepMatch[0].replace(/KEEP[^:]*:?\s*/i, "").trim() : "";
    }
    return "";
  };

  const getGuidelineRemoveSectionValue = (guideline) => {
    if (localGuidelineRemoveSection[guideline.id] !== undefined) {
      return localGuidelineRemoveSection[guideline.id];
    }
    if (guideline.removeSection) return guideline.removeSection;
    if (guideline.content) {
      const removeMatch = guideline.content.match(/REMOVE[^]*$/i);
      return removeMatch ? removeMatch[0].replace(/REMOVE[^:]*:?\s*/i, "").trim() : "";
    }
    return "";
  };

  const handleSetDefaultCleanupGuideline = async (guidelineId) => {
    try {
      const updates = {};
      cleanupGuidelinesList.forEach((g) => {
        updates[`/World/${userId}/settings/cleanupGuidelinesList/${g.id}/isDefault`] = false;
      });
      updates[`/World/${userId}/settings/cleanupGuidelinesList/${guidelineId}/isDefault`] = true;

      await update(ref(database), updates);

      setCleanupGuidelinesList((prev) => prev.map((g) => ({ ...g, isDefault: g.id === guidelineId })));

      flashMessage("Standard Cleanup Guideline erfolgreich gesetzt.");
    } catch (error) {
      console.error("Fehler beim Setzen der Standard Cleanup Guideline:", error);
      flashMessage("Fehler beim Setzen der Standard Cleanup Guideline.");
    }
  };

  const handleDeleteCleanupGuideline = async (guidelineId) => {
    if (cleanupGuidelinesList.length === 1) {
      flashMessage("Du kannst die letzte Cleanup Guideline nicht löschen.");
      return;
    }

    setConfirmModal({
      open: true,
      title: "Delete Cleanup Guideline",
      message: "Are you sure you want to delete this cleanup guideline?",
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, open: false }));
        try {
          const guidelineToDelete = cleanupGuidelinesList.find((g) => g.id === guidelineId);
          const wasDefault = guidelineToDelete?.isDefault;

          const guidelineRef = ref(database, `/World/${userId}/settings/cleanupGuidelinesList/${guidelineId}`);
          await remove(guidelineRef);

          if (wasDefault && cleanupGuidelinesList.length > 1) {
            const remainingGuidelines = cleanupGuidelinesList.filter((g) => g.id !== guidelineId);
            const newDefaultGuideline = remainingGuidelines[0];
            if (newDefaultGuideline) {
              const newDefaultRef = ref(
                database,
                `/World/${userId}/settings/cleanupGuidelinesList/${newDefaultGuideline.id}/isDefault`
              );
              await set(newDefaultRef, true);
            }
          }

          if (expandedGuidelineId === guidelineId) setExpandedGuidelineId(null);

          flashMessage("Cleanup Guideline erfolgreich gelöscht.");
        } catch (error) {
          console.error("Fehler beim Löschen der Cleanup Guideline:", error);
          flashMessage("Fehler beim Löschen der Cleanup Guideline.");
        }
      },
    });
  };

  const toggleGuidelineExpansion = (guidelineId) => {
    setExpandedGuidelineId((prev) => (prev === guidelineId ? null : guidelineId));
  };

  // ── Legacy Migration ──
  const migrateLegacyGuideline = (guideline) => {
    if (!guideline.content || !guideline.content.trim()) return null;

    const content = guideline.content.trim();
    let keepSection = "";
    let removeSection = "";

    const keepMatch = content.match(/KEEP[^]*?(?=REMOVE|$)/is);
    const removeMatch = content.match(/REMOVE[^]*$/is);

    if (keepMatch) {
      keepSection = keepMatch[0]
        .replace(/KEEP[^:]*:?\s*/i, "")
        .replace(/REMOVE[^]*$/is, "")
        .trim();
    }

    if (removeMatch) {
      removeSection = removeMatch[0].replace(/REMOVE[^:]*:?\s*/i, "").trim();
    }

    if (!keepMatch && !removeMatch) {
      const lines = content.split("\n").filter((line) => line.trim());
      const hasBulletPoints = lines.some((line) => line.trim().startsWith("-"));
      const removeKeywords = ["remove", "exclude", "delete", "reject", "filter out", "doctors", "lawyers", "architect"];
      const hasRemoveKeywords = removeKeywords.some((keyword) => content.toLowerCase().includes(keyword));

      if (hasRemoveKeywords || hasBulletPoints) {
        removeSection = content;
      } else {
        keepSection = content;
      }
    }

    return {
      ...guideline,
      keepSection: keepSection || "",
      removeSection: removeSection || "",
      content: undefined,
    };
  };

  const migrateAllLegacyGuidelines = async () => {
    if (legacyGuidelines.length === 0) return;

    try {
      flashMessage(`Migrating ${legacyGuidelines.length} legacy guideline(s)...`, 0);

      const updates = {};
      const removals = [];

      for (const legacyGuideline of legacyGuidelines) {
        const migrated = migrateLegacyGuideline(legacyGuideline);
        if (migrated) {
          const guidelinePath = `/World/${userId}/settings/cleanupGuidelinesList/${legacyGuideline.id}`;
          updates[`${guidelinePath}/keepSection`] = migrated.keepSection || "";
          updates[`${guidelinePath}/removeSection`] = migrated.removeSection || "";
          removals.push(ref(database, `${guidelinePath}/content`));
        }
      }

      if (Object.keys(updates).length > 0) {
        await update(ref(database), updates);
      }

      for (const removalRef of removals) {
        try {
          await remove(removalRef);
        } catch (removeError) {
          console.warn("Could not remove content field for guideline:", removeError);
        }
      }

      flashMessage(`Successfully migrated ${legacyGuidelines.length} legacy guideline(s)! They will appear shortly.`, 5000);
      setLegacyGuidelines([]);
    } catch (error) {
      console.error("Error migrating legacy guidelines:", error);
      flashMessage("Error migrating legacy guidelines: " + error.message, 5000);
    }
  };

  // ── Render ──
  return (
    <>
      <div className="cleanupMultiGuidelinesContainer lcr">
        {message && <div className="cleanupInlineMessage">{message}</div>}

        {legacyGuidelines.length > 0 && (
          <div className="cleanupLegacyWarningBanner">
            <div className="cleanupLegacyWarningContent">
              <div className="cleanupLegacyWarningIcon">
                <img src="/img/danger.svg" alt="Warning" />
              </div>
              <div className="cleanupLegacyWarningText">
                <strong>
                  {legacyGuidelines.length} legacy guideline{legacyGuidelines.length > 1 ? "s" : ""}
                </strong>{" "}
                found in the old format.
              </div>
            </div>
            <button className="cleanupMigrateLegacyButton" onClick={migrateAllLegacyGuidelines}>
              Migrate
            </button>
          </div>
        )}

        <div className="lcr-list">
          {cleanupGuidelinesList.map((guideline) => {
            const isOpen = expandedGuidelineId === guideline.id;
            const mode = getCleaningMode(guideline);
            const isEmailOnly = mode === "email";
            const createdLabel = formatCreatedAt(guideline.createdAt);
            const setupScore = getGuidelineSetupScore(guideline);
            const setupTone = getSetupScoreTone(setupScore);
            const setupLabel = getSetupScoreLabel(setupScore);
            const showCrmToggle =
              closeIntegration?.connected &&
              closeIntegration.leadSourceFieldId &&
              closeIntegration.enabled !== false;

            return (
              <article
                key={guideline.id}
                className={`lcr-card ${guideline.isDefault ? "is-default" : ""} ${isOpen ? "is-open" : ""}`}
              >
                <div className="lcr-cardHead" onClick={() => toggleGuidelineExpansion(guideline.id)}>
                  <div className="lcr-cardHeadMain">
                    <div className="lcr-cardTitleRow">
                      <span className="lcr-cardName">{guideline.name}</span>
                      {guideline.isDefault ? (
                        <span className="lcr-pill lcr-pill--default">Default</span>
                      ) : (
                        <button
                          type="button"
                          className="lcr-defaultBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefaultCleanupGuideline(guideline.id);
                          }}
                        >
                          Use as default
                        </button>
                      )}
                    </div>
                    <div className="lcr-cardMeta">
                      {createdLabel && <span>Created {createdLabel}</span>}
                      {createdLabel && (
                        <span className="lcr-cardMetaDot" aria-hidden="true">·</span>
                      )}
                      <span
                        className={`lcr-setup lcr-setup--${setupTone}`}
                        title={`How much info this set gives the cleaner (${setupScore}/10)`}
                      >
                        <span className="lcr-setupLabel">{setupLabel}</span>
                        <span className="lcr-setupBar" aria-hidden="true">
                          <span
                            className="lcr-setupBarFill"
                            style={{ width: `${(setupScore / 10) * 100}%` }}
                          />
                        </span>
                        <span className="lcr-setupScore">{setupScore}/10</span>
                      </span>
                    </div>
                  </div>
                  <span className="lcr-expandIcon" aria-hidden="true">
                    <img
                      src={downArrow}
                      alt=""
                      style={{
                        width: 16,
                        height: 16,
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                  </span>
                </div>

                {isOpen && (
                  <div className="lcr-cardBody">
                    <label className="lcr-field">
                      <span className="lcr-fieldLabel">Set name</span>
                      <input
                        type="text"
                        value={guideline.name}
                        onChange={(e) => handleUpdateCleanupGuideline(guideline.id, "name", e.target.value)}
                        className="lcr-input"
                        placeholder="Guideline name"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </label>

                    <section className="lcr-section">
                      <header className="lcr-sectionHead">
                        <h4>Cleaning mode</h4>
                        <p>How this set processes each contact</p>
                      </header>
                      <div className="lcr-modes">
                        {CLEANING_MODES.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className={`lcr-mode ${mode === item.id ? "is-on" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetCleaningMode(guideline.id, item.id);
                            }}
                          >
                            <span className="lcr-modeIcon">
                              <i className={`fa ${item.icon}`} />
                            </span>
                            <span className="lcr-modeTitle">{item.title}</span>
                            <span className="lcr-modeText">{item.text}</span>
                          </button>
                        ))}
                      </div>
                    </section>

                    {isEmailOnly ? (
                      <div className="lcr-note">
                        <i className="fa fa-circle-info" />
                        <span>
                          This set only checks emails. KEEP/REMOVE rules and name cleaning are skipped.
                        </span>
                      </div>
                    ) : (
                      <>
                        <label className="lcr-field">
                          <span className="lcr-fieldLabel">
                            Describe what you sell
                            <span className="lcr-fieldHint">
                              One sentence so the AI understands your perspective
                            </span>
                          </span>
                          <input
                            type="text"
                            value={
                              localGuidelineBusinessContext[guideline.id] !== undefined
                                ? localGuidelineBusinessContext[guideline.id]
                                : guideline.businessContext || ""
                            }
                            onChange={(e) => handleLocalBusinessContextChange(guideline.id, e.target.value)}
                            onBlur={(e) => handleBusinessContextBlur(guideline.id, e.target.value)}
                            className="lcr-input"
                            placeholder="e.g. We sell kitchen design software to interior designers and kitchen studios"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </label>

                        <div className="lcr-rules">
                          <div className="lcr-rule lcr-rule--keep">
                            <div className="lcr-ruleHead">
                              <span className="lcr-ruleBadge lcr-ruleBadge--keep">
                                <i className="fa fa-check" />
                                Keep
                              </span>
                              <span className="lcr-ruleHint">Businesses to keep</span>
                            </div>
                            <textarea
                              value={getGuidelineKeepSectionValue(guideline)}
                              onChange={(e) => handleLocalGuidelineKeepSectionChange(guideline.id, e.target.value)}
                              onBlur={(e) => handleGuidelineKeepSectionBlur(guideline.id, e.target.value)}
                              className="lcr-textarea lcr-textarea--keep"
                              rows="8"
                              placeholder="- Küchendesign&#10;- Tischlerei&#10;- Möbeldesign&#10;- Interior Design"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="lcr-rule lcr-rule--remove">
                            <div className="lcr-ruleHead">
                              <span className="lcr-ruleBadge lcr-ruleBadge--remove">
                                <i className="fa fa-xmark" />
                                Remove
                              </span>
                              <span className="lcr-ruleHint">Businesses to exclude</span>
                            </div>
                            <textarea
                              value={getGuidelineRemoveSectionValue(guideline)}
                              onChange={(e) => handleLocalGuidelineRemoveSectionChange(guideline.id, e.target.value)}
                              onBlur={(e) => handleGuidelineRemoveSectionBlur(guideline.id, e.target.value)}
                              className="lcr-textarea lcr-textarea--remove"
                              rows="8"
                              placeholder="- Architekt&#10;- Gartenlandschaftsbau&#10;- PR-Agentur&#10;- Insurance"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <section className="lcr-section">
                      <header className="lcr-sectionHead">
                        <h4>Extra checks</h4>
                        <p>Optional steps after the cleaning mode above</p>
                      </header>
                      <div className="lcr-options">
                        {!isEmailOnly && (
                          <OptionToggle
                            on={guideline.cleanNames !== false}
                            icon="fa-signature"
                            title="Clean business names"
                            text="Tidy messy company names for kept contacts — drop GmbH, Ltd and extra noise."
                            onToggle={(e) => {
                              e.stopPropagation();
                              handleUpdateCleanupGuideline(
                                guideline.id,
                                "cleanNames",
                                guideline.cleanNames === false
                              );
                            }}
                          />
                        )}
                        <OptionToggle
                          on={!guideline.keepEmptyEmails}
                          icon="fa-envelope"
                          title="Remove if no email"
                          text="Contacts without an email address get dropped."
                          onToggle={(e) => {
                            e.stopPropagation();
                            handleUpdateCleanupGuideline(guideline.id, "keepEmptyEmails", !guideline.keepEmptyEmails);
                          }}
                        />
                        {showCrmToggle && (
                          <OptionToggle
                            on={guideline.crmCheckEnabled !== false}
                            icon="fa-shield-halved"
                            title="Check against Close CRM"
                            text={`Block domains already in Close with a lead source other than ${closeIntegration.whitelistValue || "SalesGod"}.`}
                            onToggle={(e) => {
                              e.stopPropagation();
                              handleUpdateCleanupGuideline(
                                guideline.id,
                                "crmCheckEnabled",
                                guideline.crmCheckEnabled === false
                              );
                            }}
                          />
                        )}
                      </div>
                    </section>

                    {cleanupGuidelinesList.length > 1 && (
                      <div className="lcr-cardFoot">
                        <button
                          type="button"
                          className="lcr-deleteBtn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCleanupGuideline(guideline.id);
                          }}
                        >
                          Delete set
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {showAddCleanupGuideline ? (
          <div className="cleanupAddGuidelineInline">
            <input
              type="text"
              value={newGuidelineName}
              onChange={(e) => setNewGuidelineName(e.target.value)}
              placeholder="Name for new guideline set..."
              className="cleanupAddInlineInput"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && newGuidelineName.trim()) handleAddCleanupGuideline();
                if (e.key === "Escape") {
                  setShowAddCleanupGuideline(false);
                  setNewGuidelineName("");
                }
              }}
            />
            <button
              className="cleanupAddInlineCreate"
              onClick={handleAddCleanupGuideline}
              disabled={!newGuidelineName.trim()}
            >
              Create
            </button>
            <button
              className="cleanupAddInlineCancel"
              onClick={() => {
                setShowAddCleanupGuideline(false);
                setNewGuidelineName("");
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <button className="cleanupAddDashedButton" onClick={() => setShowAddCleanupGuideline(true)}>
            <span className="cleanupAddDashedIcon">+</span>
            Add Guideline Set
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default ListCleaningRulesSettings;
